/**
 * Elenco giocatori della societa (V35).
 *
 * L'Admin vede tutte le anagrafiche, crea e archivia; Mister e Dirigente
 * leggono i soli tesserati delle loro rose correnti, gia filtrati dal
 * server. Un ragazzo in piu rose compare una volta con tutte le squadre.
 * La riga apre la scheda (V36).
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArchiveIcon, ArchiveRestoreIcon, SearchIcon } from 'lucide-react';
import { ambitoDelPermesso } from '@calcio/shared';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from '@/components/ui/empty';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ComandoIcona, PulsanteNuovo } from '../comuni/Azioni.js';
import { chiama } from '../api/client.js';
import { StatoCaricamento, Testata } from '../comuni/Testata.js';
import { Corpo } from '../comuni/Corpo.js';
import { Filtro } from '../comuni/FiltriVista.js';
import { messaggio, useDati } from '../comuni/dati.js';
import { useContesto } from '../navigazione/contesto.js';
import { type Filtri, type Giocatore, annoDi, filtraGiocatori } from './elenchi.js';
import { BadgeStati } from './Stati.js';

export function ElencoGiocatori() {
  const { utente } = useContesto();
  const admin = ambitoDelPermesso(utente.assegnazioni, 'giocatore.gestisci') === 'societa';
  const [archiviati, setArchiviati] = useState(false);
  const {
    dati: giocatori,
    errore,
    ricarica,
  } = useDati<Giocatore[]>(`/giocatori${archiviati ? '?archiviati=true' : ''}`);
  const [filtri, setFiltri] = useState<Filtri>({ testo: '', squadra: 'tutte', stato: 'tutti' });

  const nuovo = admin && <PulsanteNuovo etichetta="Nuovo giocatore" a="/giocatori/nuovo" />;

  if (!giocatori)
    return (
      <Corpo>
        <StatoCaricamento errore={errore} />
      </Corpo>
    );

  const archivia = async (g: Giocatore, archiviato: boolean) => {
    try {
      await chiama(`/giocatori/${g.id}/archivio`, { metodo: 'POST', corpo: { archiviato } });
      await ricarica();
      toast.success(`${g.cognome} ${g.nome} ${archiviato ? 'archiviato' : 'ripristinato'}.`);
    } catch (e) {
      toast.error(messaggio(e));
    }
  };

  // Le squadre del filtro sono quelle che compaiono nell'elenco, non tutte.
  const squadre = [...new Map(giocatori.flatMap((g) => g.squadre).map((s) => [s.id, s.nome]))].sort(
    ([, a], [, b]) => a.localeCompare(b, 'it'),
  );
  const filtrati = filtraGiocatori(giocatori, filtri);

  return (
    <>
      <Testata titolo="Giocatori" voce="giocatori" azione={giocatori.length > 0 && nuovo} />
      <Corpo>
        {giocatori.length === 0 && !archiviati ? (
          <Empty>
            <EmptyHeader>
              <EmptyTitle>Nessun giocatore</EmptyTitle>
              <EmptyDescription>
                {admin
                  ? 'Le anagrafiche sono della società: si creano una volta e si tesserano nelle rose.'
                  : 'Qui compaiono i tesserati delle tue rose della stagione corrente.'}
              </EmptyDescription>
            </EmptyHeader>
            {nuovo && <EmptyContent>{nuovo}</EmptyContent>}
          </Empty>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-3">
              <InputGroup className="min-w-56 flex-[2]">
                <InputGroupInput
                  placeholder="Cerca per nome o cognome"
                  aria-label="Cerca per nome o cognome"
                  value={filtri.testo}
                  onChange={(e) => setFiltri((f) => ({ ...f, testo: e.target.value }))}
                />
                <InputGroupAddon>
                  <SearchIcon />
                </InputGroupAddon>
              </InputGroup>
              <Filtro
                etichetta="Squadra"
                valore={filtri.squadra}
                onCambia={(squadra) => setFiltri((f) => ({ ...f, squadra }))}
                tutti="tutte"
                opzioni={[
                  { valore: 'tutte', etichetta: 'Tutte' },
                  ...squadre.map(([valore, etichetta]) => ({ valore, etichetta })),
                  { valore: 'nessuna', etichetta: 'Senza squadra' },
                ]}
              />
              <Filtro
                etichetta="Stato"
                valore={filtri.stato}
                onCambia={(stato) => setFiltri((f) => ({ ...f, stato: stato as Filtri['stato'] }))}
                opzioni={[
                  { valore: 'tutti', etichetta: 'Tutti' },
                  { valore: 'non_disponibili', etichetta: 'Non disponibili' },
                  { valore: 'diffidati', etichetta: 'Diffidati' },
                  { valore: 'squalificati', etichetta: 'Squalificati' },
                ]}
              />
              {admin && (
                <div className="flex items-center gap-2">
                  <Switch id="archiviati" checked={archiviati} onCheckedChange={setArchiviati} />
                  <Label htmlFor="archiviati" className="font-normal">
                    Archiviati
                  </Label>
                </div>
              )}
            </div>

            <p className="text-sm text-muted-foreground tabular-nums">
              {filtrati.length === giocatori.length
                ? `${giocatori.length} giocatori`
                : `${filtrati.length} di ${giocatori.length} giocatori`}
            </p>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Giocatore</TableHead>
                  <TableHead>Squadre</TableHead>
                  <TableHead>Stati</TableHead>
                  {admin && (
                    <TableHead>
                      <span className="sr-only">Azioni</span>
                    </TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtrati.map((g) => (
                  <TableRow key={g.id}>
                    <TableCell>
                      <Link to={`/giocatori/${g.id}`} className="flex flex-col hover:underline">
                        <span className="font-medium">
                          {g.cognome} {g.nome}
                        </span>
                        <span className="text-xs text-muted-foreground tabular-nums">
                          {annoDi(g.dataNascita)}
                        </span>
                      </Link>
                    </TableCell>
                    <TableCell>
                      <span className="flex flex-wrap gap-1">
                        {g.squadre.map((s) => (
                          <Badge key={s.id} variant="outline">
                            {s.nome}
                          </Badge>
                        ))}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="flex flex-wrap gap-1">
                        {g.archiviato && <Badge variant="secondary">Archiviato</Badge>}
                        <BadgeStati stato={g.stato} statiSportivi={g.statiSportivi} />
                      </span>
                    </TableCell>
                    {admin && (
                      <TableCell>
                        <div className="flex justify-end">
                          <ComandoIcona
                            etichetta={`${g.archiviato ? 'Ripristina' : 'Archivia'} ${g.cognome} ${g.nome}`}
                            Icona={g.archiviato ? ArchiveRestoreIcon : ArchiveIcon}
                            size="icon-sm"
                            onClick={() => void archivia(g, !g.archiviato)}
                          />
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Corpo>
    </>
  );
}
